import styles from './countdown.module.scss';
import { useCountdown } from './useCountdown';
import { Icon } from '../icons/Icon';
import { ColorTypesEnum } from '@utmb/types/colors';
import { IconsEnum } from '@utmb/types/icons';
import { getColorStyle } from '@utmb/utils/colors';
import { toDate } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { ReactElement } from 'react';

import { Font } from '../text/font';

interface CountdownProps {
    targetDate: Date;
    variant?: 'default' | 'tudor';
}

// Tudor brand red (matches the Tudor logo color)
const TUDOR_RED = '#dc0a14';

const Countdown = ({ targetDate, variant = 'default' }: CountdownProps): ReactElement => {
    const [isDateInPast, isLessThanTwoMinutes, days, hours, daysAndHours, minutes, seconds] = useCountdown(toDate(targetDate));

    const { t } = useTranslation();

    const isTudor = variant === 'tudor';

    // Tudor variant uses Futura (lighter weight for the figures); default keeps Oxanium.
    const NumberFont = isTudor ? Font.FuturaHeavy : Font.OxaniumBold;
    const LabelFont = isTudor ? Font.FuturaMedium : Font.OxaniumMedium;

    // Tudor variant: black numbers with the largest non-zero unit in red, bigger font, no glass panel. Labels stay unchanged.
    const panelClass = isTudor ? styles.tudorCountdown : styles.transparentCountdown;
    const numberColor = getColorStyle(isTudor ? ColorTypesEnum.BLACK : ColorTypesEnum.WHITE);
    const labelColor = getColorStyle(isTudor ? ColorTypesEnum.GREY_TEXT : ColorTypesEnum.WHITE);

    const leadingUnit = days > 0 ? 'days' : +hours > 0 ? 'hours' : +minutes > 0 ? 'minutes' : 'seconds';
    const unitColor = (unit: 'days' | 'hours' | 'minutes' | 'seconds'): string => (isTudor && unit === leadingUnit ? getColorStyle(TUDOR_RED) : numberColor);
    const numberDesktop = isTudor ? '60' : '18';
    const numberMobile = isTudor ? '32' : '18';
    const labelDesktop = isTudor ? '14' : '10';
    const labelMobile = isTudor ? '12' : '10';

    return (
        <div className={styles.countdownContainer}>
            <>
                <div className={panelClass}>
                    {days > 0 ? (
                        <div className={styles.wrapUnity}>
                            <NumberFont className={styles.boldText} as="span" desktop={numberDesktop} mobile={numberMobile} color={unitColor('days')}>
                                {days}
                            </NumberFont>
                            <LabelFont className={styles.smallText} as="span" desktop={labelDesktop} mobile={labelMobile} color={labelColor}>
                                {t('countdown.suffixDayRemain', {
                                    count: +days,
                                    ordinal: false,
                                })}
                            </LabelFont>
                        </div>
                    ) : null}
                    <div className={styles.wrapUnity}>
                        <NumberFont className={styles.boldText} as="span" desktop={numberDesktop} mobile={numberMobile} color={unitColor('hours')}>
                            {hours}
                        </NumberFont>
                        <LabelFont className={styles.smallText} as="span" desktop={labelDesktop} mobile={labelMobile} color={labelColor}>
                            {t('countdown.suffixHourRemain', {
                                count: +hours === 0 ? 1 : +hours,
                                ordinal: false,
                            })}
                        </LabelFont>
                    </div>
                    <div className={styles.wrapUnity}>
                        <NumberFont className={styles.boldText} as="span" desktop={numberDesktop} mobile={numberMobile} color={unitColor('minutes')}>
                            {minutes}
                        </NumberFont>
                        <LabelFont className={styles.smallText} as="span" desktop={labelDesktop} mobile={labelMobile} color={labelColor}>
                            {t('countdown.suffixMinutesRemain', {
                                count: +minutes === 0 ? 1 : +minutes,
                                ordinal: false,
                            })}
                        </LabelFont>
                    </div>
                    <div className={styles.wrapUnity}>
                        <NumberFont className={styles.boldText} as="span" desktop={numberDesktop} mobile={numberMobile} color={unitColor('seconds')}>
                            {seconds}
                        </NumberFont>
                        <LabelFont className={styles.smallText} as="span" desktop={labelDesktop} mobile={labelMobile} color={labelColor}>
                            {t('countdown.suffixSecondRemain', {
                                count: +seconds === 0 ? 1 : +seconds,
                                ordinal: false,
                            })}
                        </LabelFont>
                    </div>
                </div>
            </>
        </div>
    );
};

export default Countdown;
